import { useCallback, useEffect, useState } from "react";
import { Editor } from "./Editor";
import { Home } from "./Home";
import { createProject, type PhotoFolioProject } from "../domain/project";
import { listProjects, saveProject } from "../storage/database";
import { importPhoto } from "../media/process";
import { importProjectArchive } from "../exporters/project-exporter";

const demoFiles = [
  "F2386E182C552409DE8669729F53C0A1.png",
  "EC84E700AA83870327C83391CBDC8F6F.png",
  "CF1B8FE79EFF200873E08A5893BEE0C4.png",
  "C5283110417ECBD27A09FE65429FA381.png",
  "C48A7D11D9ED680EB0462E9418137E48.png",
];

export function App() {
  const [projects, setProjects] = useState<PhotoFolioProject[]>([]);
  const [active, setActive] = useState<PhotoFolioProject | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => setProjects(await listProjects()), []);

  useEffect(() => {
    refresh().finally(() => setLoading(false));
  }, [refresh]);

  const openProject = async (project: PhotoFolioProject) => {
    const opened = { ...project, lastOpenedAt: new Date().toISOString() };
    await saveProject(opened);
    setActive(opened);
  };

  const createNew = async (details: { title: string; subtitle: string; date: string; location: string; people: string[] }) => {
    const project = { ...createProject(details.title), ...details };
    await saveProject(project);
    setProjects((current) => [project, ...current]);
    setActive(project);
  };

  const importDraft = async (file: File) => {
    try {
      const project = await importProjectArchive(file);
      await refresh();
      setActive(project);
    } catch (error) {
      window.alert(error instanceof Error ? error.message : "草稿导入失败");
    }
  };

  const createDemo = async () => {
    const project = createProject("夜航手记");
    project.subtitle = "五张纸上影像，一次完整的本地保存与离线导出验证。";
    project.location = "窗边与旷野";
    await saveProject(project);
    try {
      const items = [];
      for (const [index, name] of demoFiles.entries()) {
        const response = await fetch(`./sample/${name}`);
        if (!response.ok) throw new Error("示例图片尚未准备好");
        const file = new File([await response.blob()], name, { type: "image/png" });
        const result = await importPhoto(project.id, file, index);
        result.item.title = ["夜色停靠", "安静轨道", "窗边停顿", "柔软天气", "远处的光"][index];
        result.item.caption = ["山脊把夜空托得很低。", "月亮从两片树影之间经过。", "光落在百叶窗上，房间暂时没有时间。", "天空褪成纸上的两种颜色。", "把没有说完的话留给下一页。"][index];
        items.push(result.item);
      }
      const completed = { ...project, items, coverAssetId: items[0]?.assetId, updatedAt: new Date().toISOString() };
      await saveProject(completed);
      await refresh();
      setActive(completed);
    } catch (error) {
      window.alert(error instanceof Error ? error.message : "示例创建失败");
    }
  };

  if (loading) return <div className="app-loading"><div /><span>正在打开本地影集</span></div>;

  return active ? (
    <Editor
      key={active.id}
      initialProject={active}
      onBack={() => { setActive(null); refresh(); }}
      onProjectChange={(project) => setProjects((current) => current.map((item) => item.id === project.id ? project : item))}
    />
  ) : <Home projects={projects} onCreate={createNew} onOpen={openProject} onImport={importDraft} onDemo={createDemo} />;
}
